import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { ProjectAvailability, RegisteredApp } from "@/services/projectAvailabilityApi";

/**
 * Projects x registered apps grid: one switch per (project, app) saying whether
 * the project is available in that app. Presentational only (data in, callbacks
 * out) so it can move to @lumanity/ui unchanged.
 */
interface ProjectAvailabilityTableProps {
  apps: RegisteredApp[];
  projects: ProjectAvailability[];
  /** The app this UI runs in (highlighted). */
  currentAppId?: string;
  isLoading?: boolean;
  /** "projectId:appId" of switches with a request in flight. */
  pending?: Set<string>;
  onChange: (projectId: string, appId: string, available: boolean) => void;
}

const appName = (apps: RegisteredApp[], id: string) => apps.find((a) => a.id === id)?.name ?? id;

export const ProjectAvailabilityTable = ({
  apps,
  projects,
  currentAppId,
  isLoading,
  pending,
  onChange,
}: ProjectAvailabilityTableProps) => {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) =>
      [p.name, p.code, p.client_name, p.owner_app_id].some((v) => v?.toLowerCase().includes(q))
    );
  }, [projects, query]);

  const columns = 3 + apps.length;

  return (
    <div className="space-y-4">
      <div className="relative max-w-sm">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search projects…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-8"
          aria-label="Search projects"
        />
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Project</TableHead>
              <TableHead>Client</TableHead>
              <TableHead className="w-32">Owning app</TableHead>
              {apps.map((app) => (
                <TableHead key={app.id} className="w-28 text-center">
                  {app.name}
                  {app.id === currentAppId && <span className="block text-[10px] font-normal text-muted-foreground">this app</span>}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={columns} className="text-center text-muted-foreground py-8">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!isLoading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns} className="text-center text-muted-foreground py-8">
                  No projects found.
                </TableCell>
              </TableRow>
            )}
            {filtered.map((project) => (
              <TableRow key={project.id}>
                <TableCell>
                  <div className="font-medium">{project.name}</div>
                  <div className="text-xs text-muted-foreground">{project.code}</div>
                </TableCell>
                <TableCell className="text-muted-foreground">{project.client_name}</TableCell>
                <TableCell>
                  <Badge variant="secondary">{appName(apps, project.owner_app_id)}</Badge>
                </TableCell>
                {apps.map((app) => {
                  const link = project.apps.find((a) => a.app_id === app.id);
                  const isOwner = link?.access === "owner";
                  const busy = pending?.has(`${project.id}:${app.id}`) ?? false;
                  const disabled = isOwner || !project.can_manage || busy;
                  const reason = isOwner
                    ? "Owning app: always available"
                    : !project.can_manage
                      ? "Only the project's owners (in the owning app) or a platform admin can change this"
                      : undefined;
                  const toggle = (
                    <Switch
                      checked={Boolean(link)}
                      disabled={disabled}
                      onCheckedChange={(checked) => onChange(project.id, app.id, checked)}
                      aria-label={`${project.name} available in ${app.name}`}
                    />
                  );
                  return (
                    <TableCell key={app.id} className="text-center">
                      {reason ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex">{toggle}</span>
                          </TooltipTrigger>
                          <TooltipContent>{reason}</TooltipContent>
                        </Tooltip>
                      ) : (
                        toggle
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default ProjectAvailabilityTable;
